import React, { useMemo } from "react";
import { decimalToPercentage } from "../../../lib/tierHelper";
import { getWowStatic } from "hooks/wow/getStaticData";
import WowTooltip from "../../Tooltips/WowTooltip";
import Icon from "components/Common/Icon";
import { TooltipContainer } from "@outplayed/tooltips";
import { ReactComponent as TooltipIcon } from "svg/question-line.svg";
import { getIsHealer, getIsTank } from "../../../lib/wowHelpers";
import { sigfigFormatter } from "../../../lib/tierHelper";
import useSorting from "hooks/useSorting";
import classNames from "classnames";

export const getPrimaryMetric = (specName, encounterType) => {
  let metricOne = "DPS";
  if (getIsHealer(specName)) {
    metricOne = "HPS";
  } else if (getIsTank(specName)) {
    metricOne = "Tank DPS";
  }
  if (encounterType.includes("mythic")) {
    metricOne = "Max Key";
  }
  if (encounterType.toLowerCase() === "pvp") {
    metricOne = "Max Rating";
  }

  return metricOne;
};

const GearContainer = ({ displaySpecName, encounterType, classSpecGear }) => {
  const metricOne = getPrimaryMetric(displaySpecName, encounterType);

  let titles = {
    weapon1: "Main Hand",
    weapon2: "Off Hand",
    trinket1: "Trinkets",
    head: "Head",
    neck: "Neck",
    shoulder: "Shoulders",
    cape: "Back",
    chest: "Chest",
    wrist: "Wrist",
    gloves: "Gloves",
    belt: "Belt",
    legs: "Legs",
    feet: "Feet",
    ring1: "Rings",
  };

  return classSpecGear ? (
    <div className="wow-items-page">
      {Object.keys(titles).map((key) => {
        // Do not show offhand if the offhand item id is set to 0 as that how our back end returns data.
        if (classSpecGear.items[key]?.items.length === 1 && classSpecGear.items[key]?.items[0].item_id === 0) {
          return null;
        } else {
          return (
            <ItemsSubContainerCategory
              key={key}
              category={titles[key]}
              metricOneName={metricOne}
              metricTwoName={"Popularity"}
              data={classSpecGear.items[key]?.items}
              count={classSpecGear.items[key]?.total_count}
            />
          );
        }
      })}
    </div>
  ) : (
    <div className="wow-items-page">
      {Object.keys(titles).map((key) => {
        return (
          <ItemsSubContainerCategory key={key} category={titles[key]} metricOneName={metricOne} metricTwoName={"Popularity"} />
        );
      })}
    </div>
  );
};

export const ItemsSubContainerCategory = (props) => {
  const { category, metricOneName, metricTwoName, data, count = 0 } = props;

  const metricOneField = useMemo(() => {
    let keyValue = "dps";
    if (metricOneName.includes("HPS")) {
      keyValue = "hps";
    } else if (metricOneName.includes("Tank DPS")) {
      keyValue = "dps";
    } else if (metricOneName.includes("Max Key")) {
      keyValue = "max_mythic_keys";
    } else if (metricOneName.includes("Max Rating")) {
      keyValue = "max_rating";
    }
    return keyValue;
  }, [metricOneName]);

  const titleToolTipText = {
    DPS: "DPS is the aggregated Damage Per Second score that each DPS spec does when using this item in the top rankings of Raid and Mythic+ Dungeons.",
    HPS: "HPS is the aggregated Healing Per Second score that each Healer spec does when using this item in the top rankings of Raid and Mythic+ Dungeons.",
    "Tank DPS":
      "For Tanks we opted to show their aggregated DPS score when using this item as a temporary placeholder metric while we search for a more valuable metric that tank players care about.",
    "Max Key": "The highest key that this particular item was used in and completed on time.",
    "Max Rating": "The highest rated player that used this particular item in the selected competitive PvP game mode.",
  };

  const fieldNames = [
    { title: metricOneName, key: metricOneField },
    { title: metricTwoName, key: "count" },
  ];
  const {
    sorted: sortedData,
    setSort,
    sortClass,
    sortMetric,
  } = useSorting({
    data: data,
    fieldNames,
    defaultSort: metricTwoName,
  });

  return (
    <div className="wow-item-container">
      <div className="wow-item-container-header">
        <div className="wow-item-container-header-title">{category}</div>
        <div onClick={() => setSort(metricOneName)} className={classNames("wow-item-container-header-title-metric-one")}>
          <span
            className={classNames({
              [sortClass]: sortMetric === metricOneName,
            })}
          >
            {metricOneName}
          </span>
          <TooltipContainer tooltipInfo={<div>{titleToolTipText[metricOneName]}</div>}>
            <div className="tooltip-container">
              <TooltipIcon className="tooltip-icon" />
            </div>
          </TooltipContainer>
        </div>
        <div onClick={() => setSort(metricTwoName)} className={classNames("wow-item-container-header-title-metric-two")}>
          <div
            className={classNames({
              [sortClass]: sortMetric === metricTwoName,
            })}
          >
            {metricTwoName}
          </div>
          <TooltipContainer tooltipInfo="Popularity is how often this item is used by players in the top rankings of Raid or Mythic+ Dungeons.">
            <div className="tooltip-container">
              <TooltipIcon className="tooltip-icon" />
            </div>
          </TooltipContainer>
        </div>
      </div>
      {data
        ? sortedData.map((item, i) => {
            if (item["item_id"] === 0) {
              return null;
            } else {
              return (
                <ItemsContainerSingle
                  key={item["item_id"].toString()}
                  nameOne={item["item_id"]}
                  itemId={item["item_id"]}
                  itemQuality={item["quality"]}
                  bonusIds={item["bonus_ids"]}
                  craftingQuality={item["crafted_quality"]}
                  gems={item["gems"]}
                  enchantId={item["enchant_id"]}
                  subtitle={item["stats"] ? item["stats"].split("/").join(" / ") : undefined}
                  metricOne={
                    metricOneName.includes("Max Key") || metricOneName.includes("Max Rating")
                      ? item[metricOneField]
                      : sigfigFormatter(item[metricOneField])
                  }
                  metricTwoTop={
                    // Newer payload blocks (crafted_gear, embellishments) precompute
                    // percent-of-players; a player can wear several crafted pieces, so
                    // a count/total fallback would use the wrong denominator there.
                    // Backend sometimes emits percents above 100 — cap until the
                    // aggregation is fixed.
                    item["percent"] != null
                      ? `${Math.min(parseFloat(item["percent"]), 100)}%`
                      : decimalToPercentage(Math.min(item.count / (count ?? 1), 1))
                  }
                  metricTwoBot={"(" + item["count"] + ")"}
                ></ItemsContainerSingle>
              );
            }
          })
        : Array.from({ length: 10 }).map((i, index) => <ItemsContainerSingle key={index} />)}
    </div>
  );
};

const ItemsContainerSingle = (props) => {
  const { itemId, itemQuality, bonusIds, craftingQuality, gems, enchantId, subtitle, metricOne, metricTwoTop, metricTwoBot } =
    props;
  const staticData = getWowStatic("item", itemId);

  const title = staticData ? (
    // <div className="wow-item-sub-container-title" style={{ color: getItemQualityColor(itemQuality) }}>
    <div className="wow-item-sub-container-title" style={{ color: itemQuality === 5 ? "#ff8000" : "rgb(163, 53, 238)" }}>
      {staticData.name}
    </div>
  ) : null;

  return staticData ? (
    <div className="wow-item-sub-container">
      <div className="wow-item-sub-container-title-wrapper">
        <div className="wow-item-sub-container-image">
          <WowTooltip
            type="item"
            id={itemId}
            bonusIds={bonusIds}
            craftingQuality={craftingQuality}
            gems={gems}
            enchant={enchantId}
          >
            <div>
              <img src={staticData.img} style={{ width: "30px", height: "30px" }} />
            </div>
          </WowTooltip>
        </div>
        {subtitle ? (
          <div className="wow-item-sub-container-title-column">
            {title}
            <div className="wow-item-sub-container-subtitle">{subtitle}</div>
          </div>
        ) : (
          title
        )}
      </div>
      <div className="wow-item-sub-container-metric-one">{metricOne}</div>
      <div className="wow-item-sub-container-metric-two">
        <div className="wow-item-sub-container-metric-two-top">{metricTwoTop}</div>
        <div className="wow-item-sub-container-metric-two-bot">{metricTwoBot}</div>
      </div>
    </div>
  ) : (
    <div className="wow-item-sub-container" id={itemId}>
      <div className="wow-item-sub-container-image"></div>
      <div className="wow-item-sub-container-title"></div>
      <div className="wow-item-sub-container-metric-one"></div>
      <div className="wow-item-sub-container-metric-two">
        <div className="wow-item-sub-container-metric-two-top"></div>
        <div className="wow-item-sub-container-metric-two-bot"></div>
      </div>
    </div>
  );
};

const ItemsContainerDouble = (props) => {
  const { nameOne, imageOne, nameTwo, imageTwo, metricOne, metricTwoTop, metricTwoBot } = props;
  return (
    <div className="wow-item-sub-container-double">
      <div className="wow-item-sub-container-double-column">
        <div className="wow-item-sub-container-double-row">
          <div className="wow-item-sub-container-image">
            <Icon iconSize="30px" />
          </div>
          <div className="wow-item-sub-container-title">{nameOne}</div>
        </div>
        <div className="wow-item-sub-container-double-row">
          <div className="wow-item-sub-container-image">
            <Icon iconSize="30px" />
          </div>
          <div className="wow-item-sub-container-title">{nameTwo}</div>
        </div>
      </div>
      <div className="wow-item-sub-container-metric-one">{metricOne}</div>
      <div className="wow-item-sub-container-metric-two">
        <div className="wow-item-sub-container-metric-two-top">{metricTwoTop}</div>
        <div className="wow-item-sub-container-metric-two-bot">{metricTwoBot}</div>
      </div>
    </div>
  );
};

export default GearContainer;
